// Firestore listener for all workspaces the current user owns or is a member of.
// Auto-selects the first workspace on load.

import React from 'react';
import { collection, onSnapshot, or, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../contexts/ToastContext';

// ─── Pure helpers (exported for direct unit testing) ───────────────────────
// Extracted so the merge/filter/retention/validation logic can be exercised
// without a full interactive React render harness (this repo's DOM-less
// node:test runner has no reconciler for driving state transitions across
// renders — see the file header comment in
// tests/store/useWorkspaceList.orMerge.test.js).

/**
 * Merge the or() snapshot with any externally-registered docs (TAWs, via
 * setExtraWorkspaces) into ONE allWorkspaces array. Snapshot docs win on an
 * id collision (they are the live, authoritative source; a registered doc is
 * a one-time get() that can go momentarily stale). Overrides from
 * updateSelectedWorkspace are applied last so they aren't overwritten by a
 * matching snapshot doc.
 */
export function mergeAllWorkspaces(snapshotDocs, extraWorkspaces, workspaceOverrides = {}) {
    const merged = new Map();
    (extraWorkspaces || []).forEach(ws => merged.set(ws.id, ws));
    (snapshotDocs || []).forEach(ws => merged.set(ws.id, ws));
    return Array.from(merged.values())
        // Drop workspaces the customer must not reach, and ONLY those.
        //
        // The case this was written for: the ownerId disjunct can still match a
        // transferred TAW, because transferTaw preserves ownerId for audit. The
        // read rule denies subsequent get()s, but the listener's cached snapshot
        // can persist, so a stale sealed TAW would otherwise linger in the
        // switcher.
        //
        // `suspended` must NOT be dropped. Under D-6 a suspended workspace is
        // read-only, not hidden: the customer keeps seeing their transactions,
        // contacts, and reports and simply cannot change them, and
        // workspaceReadable in firestore.rules admits it for exactly that
        // reason. Filtering it here contradicted the rules layer and left the
        // customer with no workspace selected at all — an empty app, and no
        // suspension banner either, since the banner reads the active
        // workspace's status.
        .filter(ws => !ws.status || ws.status === 'active' || ws.status === 'suspended')
        .map(ws => (workspaceOverrides[ws.id] ? { ...ws, ...workspaceOverrides[ws.id] } : ws))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Derive the sidebar dropdown array from allWorkspaces.
 *
 * D-04: TAWs stay OUT of the dropdown. The parent-owner marker (written only
 * for the TW owner, per D-06) excludes a TAW for the owner; teamRole
 * excludes it for EVERY viewer, owner or member — necessary once a TW MEMBER
 * can resolve TAWs too (D-02), since the parent-owner marker alone would
 * leave every TAW visible to a member at all times.
 *
 * D-05 exception: the currently-selected TAW stays visible so the switcher
 * can name it (instead of "No workspace") and the user can select the
 * parent TW to exit. At most ONE doc is exempted — every other TAW stays
 * excluded per D-04.
 */
export function deriveDropdownWorkspaces(all, selectedWorkspaceId, user) {
    if (!user) return [];
    const filtered = all.filter(
        ws => ws.memberDirectory?.[user.uid]?.teamRole !== 'parent-owner'
            && ws.teamRole !== 'team_agent'
    );
    if (selectedWorkspaceId && !filtered.some(ws => ws.id === selectedWorkspaceId)) {
        const selectedDoc = all.find(ws => ws.id === selectedWorkspaceId);
        if (selectedDoc) return [...filtered, selectedDoc];
    }
    return filtered;
}

/**
 * Retention rule for setExtraWorkspaces: entering a TAW flips
 * isParentWorkspaceOwner false at the App.js call site, so `taws` (and
 * therefore the registered array) can momentarily go back to []. Without
 * retaining the ONE doc matching the current selection, the validation
 * effect would see the selection vanish from allWorkspaces and bounce it
 * back to workspaces[0] mid-visit. Retention is bounded to exactly that one
 * doc — every other previously-registered doc is dropped, so a stale
 * sibling TAW never lingers.
 */
export function applyExtraWorkspacesRegistration(prevExtra, nextExtra, selectedWorkspaceId) {
    const nextList = Array.isArray(nextExtra) ? nextExtra : [];
    if (!selectedWorkspaceId) return nextList;
    if (nextList.some(ws => ws.id === selectedWorkspaceId)) return nextList;
    const retained = (prevExtra || []).find(ws => ws.id === selectedWorkspaceId);
    return retained ? [...nextList, retained] : nextList;
}

/**
 * Auto-select the first workspace when the current selection becomes
 * invalid; returns the SAME selectedWorkspaceId when it is still valid (or
 * there is nothing to validate against yet).
 */
export function computeValidatedSelectedWorkspaceId(workspaces, allWorkspaces, selectedWorkspaceId) {
    const isSelectedValid = allWorkspaces.some(ws => ws.id === selectedWorkspaceId);
    if (workspaces.length > 0 && !isSelectedValid) {
        return workspaces[0].id;
    }
    if (workspaces.length === 0) {
        return '';
    }
    return selectedWorkspaceId;
}

/**
 * Manages the list of workspaces available to the authenticated user.
 *
 * Subscribes to a single Firestore or() query (ownerId, members) and produces
 * a sorted list. Auto-selects the first workspace when the selected one is no
 * longer valid.
 *
 * Returns:
 *   workspaces                {Array}    all workspaces the user can access
 *   selectedWorkspaceId       {string}   currently selected workspace ID
 *   setSelectedWorkspaceId    {Function} change the selected workspace
 *   updateSelectedWorkspace   {Function} patch fields on the selected workspace locally
 *   setExtraWorkspaces        {Function} register externally-resolved workspace docs
 *                                        (e.g. TAWs) into this hook's OWN allWorkspaces
 */
export function useWorkspaceList(user) {
    const { showToast } = useToast();
    const [snapshotDocs, setSnapshotDocs] = React.useState([]);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState('');
    const [workspaceOverrides, setWorkspaceOverrides] = React.useState({});

    // D-03 (41-04, SUPERSEDED — see 41-04-SUMMARY.md): TAW docs are no longer
    // discovered via a merged onSnapshot query. 41-01's emulator spike proved
    // useTaws' where('parentWorkspaceId','==',twId) list query is DENIED by
    // security rules for both the TW owner and a TW member — a rule provable
    // for a single-document `get()` is not automatically provable for `list`
    // when the looked-up id comes from a document field. The phase owner's
    // replacement: resolve TAW ids from the parent TW doc's own
    // teamAgentWorkspaceIds field (TAW WORKSPACE DOCUMENT ids -- a
    // gap-closure fix found during live rollout corrected this from an
    // earlier, incorrect teamAgentIds/agent-uid read; see
    // src/hooks/useTaws.js's header comment), get() each TAW doc directly
    // (src/hooks/useTaws.js), and register the results here via
    // setExtraWorkspaces. This hook still
    // owns the merge into its OWN allWorkspaces state — not a caller-side
    // merge in App.js — because the :~130 validation effect below reads THIS
    // hook's allWorkspaces, and that is exactly where handleOpenTaw died
    // before this plan (Research Pitfall 2).
    const [extraWorkspaces, setExtraWorkspacesState] = React.useState([]);

    // Retention rule: entering a TAW flips isParentWorkspaceOwner false at the
    // call site, so `taws` (and therefore the registered array) can
    // momentarily go back to []. Without retaining the ONE doc matching the
    // current selection, the validation effect below would see the selection
    // vanish from allWorkspaces and bounce it back to workspaces[0] mid-visit.
    // Retention is bounded to exactly that one doc — every other previously
    // registered doc is dropped, so a stale sibling TAW never lingers.
    const setExtraWorkspaces = React.useCallback((next) => {
        setExtraWorkspacesState((prev) => applyExtraWorkspacesRegistration(prev, next, selectedWorkspaceId));
    }, [selectedWorkspaceId]);

    // Patch fields on the in-memory selected workspace without a Firestore read.
    // Stored as an override keyed by id (not a direct mutation of derived
    // state) so it survives the next snapshot/allWorkspaces recomputation.
    const updateSelectedWorkspace = React.useCallback((updates = {}) => {
        if (!selectedWorkspaceId || !updates || typeof updates !== 'object') return;
        setWorkspaceOverrides(prev => ({
            ...prev,
            [selectedWorkspaceId]: { ...(prev[selectedWorkspaceId] || {}), ...updates },
        }));
    }, [selectedWorkspaceId]);

    // ONE boot listener: or(ownerId, members array-contains) — DATA-01.
    //
    // Both disjuncts are equality/array-contains on STATIC field paths, so
    // Firestore serves them from auto-created single-field indexes and the
    // disjunction needs no composite index.
    //
    // The legacy `memberDirectory.<uid>.addedAt` predicate used to be a third
    // access path, and it can never join this or(): it is an INEQUALITY on a
    // UID-TEMPLATED field path, so Firestore demands composite indexes whose
    // field paths embed the caller's UID — one pair per user, undeclarable in
    // firestore.indexes.json. Folding it in raised "The query requires multiple
    // indexes" at runtime and blanked the entire workspace switcher (2026-07-18).
    // It has since been retired outright rather than kept as its own listener:
    // scripts/migrations/2026-07-workspace-members-backfill audited dev and prod
    // and found every memberDirectory uid already reachable via ownerId or
    // members[], and every writer that grants access maintains one of those two
    // (D-29's field-writer -> backfill -> reader invariant). Reintroducing a
    // writer that adds someone to memberDirectory ALONE would silently cost them
    // access — tests/store/useWorkspaceList.orMerge.test.js guards that.
    //
    // Split useEffect to prevent infinite loops (workspace list vs. selection).
    React.useEffect(() => {
        if (!user) {
            setSnapshotDocs([]);
            return;
        }

        const unsubscribe = onSnapshot(
            query(
                collection(db, 'workspaces'),
                or(
                    where('ownerId', '==', user.uid),
                    where('members', 'array-contains', user.uid),
                ),
            ),
            (snapshot) => {
                setSnapshotDocs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            },
            (error) => {
                console.error('Failed to subscribe to workspaces:', error);
                // Surface the failure instead of rendering an empty switcher that
                // looks identical to "you have no workspaces" — that ambiguity is
                // what made the 2026-07-18 index failure read as total data loss.
                const message = error?.code === 'permission-denied'
                    ? 'Permission denied while loading workspaces. Update workspace sharing or security rules.'
                    : 'Could not load your workspaces. Please reload; if this persists, contact support.';
                showToast(message, 'error');
            }
        );

        return unsubscribe;
    }, [user, showToast]);

    // Merge the or() snapshot with any externally-registered docs (TAWs, via
    // setExtraWorkspaces) into ONE allWorkspaces computation, so the
    // validation effect below sees a TAW as a valid selection the moment it
    // is registered.
    const allWorkspaces = React.useMemo(
        () => mergeAllWorkspaces(snapshotDocs, extraWorkspaces, workspaceOverrides),
        [snapshotDocs, extraWorkspaces, workspaceOverrides]
    );

    const workspaces = React.useMemo(
        () => deriveDropdownWorkspaces(allWorkspaces, selectedWorkspaceId, user),
        [allWorkspaces, selectedWorkspaceId, user]
    );

    // Auto-select the first workspace when the current selection becomes invalid.
    // Uses allWorkspaces so that a child workspace opened via Admin (or a
    // newly-registered TAW) stays valid.
    React.useEffect(() => {
        const nextSelectedWorkspaceId = computeValidatedSelectedWorkspaceId(workspaces, allWorkspaces, selectedWorkspaceId);
        if (nextSelectedWorkspaceId !== selectedWorkspaceId) {
            setSelectedWorkspaceId(nextSelectedWorkspaceId);
        }
    }, [workspaces, allWorkspaces]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        workspaces,
        allWorkspaces,
        selectedWorkspaceId,
        setSelectedWorkspaceId,
        updateSelectedWorkspace,
        setExtraWorkspaces,
    };
}
